import { z } from 'zod';
import { GoLiveChecklist, GoLiveStepId } from './admin.js';
import { ProgramName } from './sector-setup.js';

/**
 * The starting setup file (APP-118): one strict, versioned JSON file that names the first role
 * holders and the go-live inputs, uploaded on the Go-live checklist or run by the deployment's
 * `starting-setup.js`. Every field is checked here; an unknown field anywhere refuses the file
 * (D1). Sector, pool and setting references are checked against the environment by the server.
 */
export const STARTING_SETUP_FORMAT = 'myrewards.starting-setup';
export const STARTING_SETUP_VERSION = 1;
/** The same caps as the CSV imports: 2 MB of UTF-8. */
export const STARTING_SETUP_MAX_BYTES = 2 * 1024 * 1024;

const Name = z.string().trim().min(1).max(200);
/** A baseline id: `maritime-airborne`, `paa-taa`. */
const RefId = z.string().trim().min(1).max(100);
const Count = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const Year = z.number().int().min(2000).max(2100);
/** Project numbers, org codes and EE#s are always text, so 15125.110 keeps its zero. */
const Code = z.string().trim().min(1).max(40);

function realDate(value: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m === null) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d;
}
const SetupDate = z.string().refine(realDate, 'use a real date written as YYYY-MM-DD');

/** How the file names a person (D3): their name, as a check, and one or more keys. */
const PERSON_KEYS = {
  name: Name,
  signInName: z.string().trim().min(3).max(254).optional(),
  email: z.email().max(254).optional(),
  eeNumber: Code.optional(),
  entraObjectId: z.guid().optional(),
};
export const SETUP_PERSON_KEY_MESSAGE = 'add a sign-in name, work email, EE# or Entra object ID';

interface PersonKeyFields {
  signInName?: string | undefined;
  email?: string | undefined;
  eeNumber?: string | undefined;
  entraObjectId?: string | undefined;
}
const hasKey = (p: PersonKeyFields): boolean =>
  p.signInName !== undefined ||
  p.email !== undefined ||
  p.eeNumber !== undefined ||
  p.entraObjectId !== undefined;

function person<S extends z.ZodRawShape>(extra: S) {
  return z
    .object({ ...PERSON_KEYS, ...extra })
    .strict()
    .refine((p) => hasKey(p as PersonKeyFields), { message: SETUP_PERSON_KEY_MESSAGE });
}

export const SetupPerson = person({});
export type SetupPerson = z.infer<typeof SetupPerson>;

/** App-owned fields only (the directory-fields import's rules); Entra's fields are refused. */
export const SetupPeopleEntry = person({
  sector: RefId.optional(),
  homeOrg: Code.optional(),
  costType: z.enum(['OH', 'GA']).optional(),
  hireDate: SetupDate.optional(),
  serviceDate: SetupDate.optional(),
  customFields: z.record(z.string().min(1).max(40), z.string().max(200)).optional(),
});
export type SetupPeopleEntry = z.infer<typeof SetupPeopleEntry>;

/** A delegate, with the screen's defaults when a field is left out: every type, no approval, on. */
export const SetupDelegate = person({
  awardTypes: z
    .array(z.enum(['SDB', 'PAA', 'TAA']))
    .min(1)
    .max(3)
    .optional(),
  needsApproval: z.boolean().optional(),
  on: z.boolean().optional(),
});
export type SetupDelegate = z.infer<typeof SetupDelegate>;

/**
 * APP-274: someone an Executive names to enter awards for them, with the screen's defaults when a
 * field is left out: every type, and no approval needed.
 */
export const SetupExecutiveDelegate = person({
  executive: SetupPerson,
  awardTypes: z
    .array(z.enum(['EDB', 'SDB', 'PAA', 'TAA']))
    .min(1)
    .max(4)
    .optional(),
  needsApproval: z.boolean().optional(),
});
export type SetupExecutiveDelegate = z.infer<typeof SetupExecutiveDelegate>;

export const SetupSector = z
  .object({
    lead: SetupPerson.optional(),
    delegates: z.array(SetupDelegate).max(500).optional(),
    programs: z.array(ProgramName).max(500).optional(),
  })
  .strict();

const People = z.array(SetupPerson).max(500);
const OhGa = z.object({ OH: Code, GA: Code }).strict();

export const StartingSetupFile = z
  .object({
    /** A JSON Schema reference for editors; ignored by MyRewards. */
    $schema: z.string().max(500).optional(),
    /** Notes for whoever fills the file in (the blank file lists the ids here); ignored. */
    $comment: z.union([z.string().max(4000), z.array(z.string().max(1000)).max(50)]).optional(),
    format: z.literal(STARTING_SETUP_FORMAT),
    version: z.literal(STARTING_SETUP_VERSION),
    /** A short name shown in the preview, the audit and the history. */
    label: z.string().trim().min(1).max(80).optional(),
    people: z.array(SetupPeopleEntry).max(5000).optional(),
    executives: People.optional(),
    sectors: z.record(RefId, SetupSector).optional(),
    roles: z
      .object({
        stockAdmin: People.optional(),
        payroll: People.optional(),
        finance: People.optional(),
        systemAdmin: People.optional(),
        paaTaaApprover: People.optional(),
        spotReviewer: People.optional(),
      })
      .strict()
      .optional(),
    /**
     * APP-274: the old requester list. Still read, so an older file loads, but it no longer gives
     * anyone access: it never said which Executive they enter for. Use `executiveDelegates`.
     */
    requesters: People.optional(),
    executiveDelegates: z.array(SetupExecutiveDelegate).max(500).optional(),
    previewTesters: z
      .object({
        /** The Preview as master switch: set only if nobody has saved Preview access yet. */
        enabled: z.boolean().optional(),
        people: z
          .array(person({ endsOn: SetupDate.nullable().optional() }))
          .max(500)
          .optional(),
      })
      .strict()
      .optional(),
    values: z
      .object({
        fmv: z
          .object({ cents: Count.min(1), from: SetupDate })
          .strict()
          .optional(),
        /** APP-151: the CFO's value of one option, which a PAA's options are counted in. */
        optionValue: z
          .object({ cents: Count.min(1), from: SetupDate })
          .strict()
          .optional(),
      })
      .strict()
      .optional(),
    chargeCodes: z
      .object({
        accounts: z.object({ discretionary: OhGa, paa_taa: OhGa, spot: OhGa }).strict().optional(),
        poolProjects: z.record(RefId, OhGa).optional(),
      })
      .strict()
      .optional(),
    notifications: z
      .object({ executiveAddress: z.email().max(254).optional() })
      .strict()
      .optional(),
    signIn: z
      .object({ splash: z.enum(['video', 'photo_wall']).optional() })
      .strict()
      .optional(),
    budgets: z
      .array(z.object({ fiscalYear: Year, pool: RefId, cashCents: Count, options: Count }).strict())
      .max(500)
      .optional(),
    /**
     * APP-205: the CEO / President who signs PAA, TAA and Spot letters (set only while the seat
     * is empty, and they must be an Executive), and the letter pack this environment starts with,
     * pinned by its SHA-256. The pack's .zip travels beside the file.
     */
    letters: z
      .object({
        ceo: SetupPerson.optional(),
        pack: z
          .object({ sha256: z.string().regex(/^[0-9a-f]{64}$/, 'use the 64-character SHA-256') })
          .strict()
          .optional(),
      })
      .strict()
      .optional(),
    /** Any other setting the registry marks portable (D10), by its key and scope. */
    settings: z
      .array(
        z
          .object({
            key: z.string().min(1).max(100),
            scope: z.string().min(1).max(100).optional(),
            value: z.unknown(),
            from: SetupDate.optional(),
          })
          .strict(),
      )
      .max(500)
      .optional(),
  })
  .strict();
export type StartingSetupFile = z.infer<typeof StartingSetupFile>;

// ---------------------------------------------------------------------------------------------
// The card on the Go-live checklist (D9)
// ---------------------------------------------------------------------------------------------

const Ref = z.object({ id: z.string(), name: z.string() });
const DateTime = z.string();

/** What happens to one entry: each is shown with a word and an icon as well as a colour. */
export const SetupEntryStatus = z.enum(['will_change', 'already_set', 'kept', 'cant_apply']);
export type SetupEntryStatus = z.infer<typeof SetupEntryStatus>;

export const SetupSection = z.enum([
  'people',
  'executives',
  'roles',
  'sector_leads',
  'delegates',
  'requesters',
  'executive_delegates',
  'preview_testers',
  'values',
  'charge_codes',
  'notifications',
  'sign_in',
  'settings',
  'programs',
  'budgets',
  'letter_ceo',
  'letter_pack',
]);
export type SetupSection = z.infer<typeof SetupSection>;

/** The preview's groups: the go-live steps the file fills, then the setup no step counts. */
export const SetupGroupId = z.enum([
  ...GoLiveStepId.options,
  'delegates',
  'requesters',
  'executive_delegates',
  'preview_testers',
  'programs',
  'settings',
]);
export type SetupGroupId = z.infer<typeof SetupGroupId>;

export const SetupEntry = z.object({
  /** Where it is in the file: `executives[2]`, `sectors.terrestrial.lead`. */
  place: z.string(),
  section: SetupSection,
  status: SetupEntryStatus,
  /** Who or what it's about: a person's name, a sector, "FMV". */
  subject: z.string(),
  /** What it sets: "Executive", "Sector Lead · Terrestrial", "Home org". */
  what: z.string(),
  /** The key that found the person. */
  matchedOn: z.enum(['sign_in_name', 'email', 'ee_number', 'entra_object_id']).nullable(),
  before: z.string().nullable(),
  after: z.string().nullable(),
  /** Why it's kept or can't be applied yet, or a note (an earlier removal, a later start). */
  message: z.string().nullable(),
  /** Where the value can be changed by hand. */
  page: z.object({ label: z.string(), href: z.string() }).nullable(),
  /** A value that starts later: the checklist counts it from then. */
  takesEffect: z.string().nullable(),
});
export type SetupEntry = z.infer<typeof SetupEntry>;

export const SetupCounts = z.object({
  willChange: z.number().int().nonnegative(),
  alreadySet: z.number().int().nonnegative(),
  kept: z.number().int().nonnegative(),
  cantApply: z.number().int().nonnegative(),
});
export type SetupCounts = z.infer<typeof SetupCounts>;

export const SetupGroup = z.object({
  id: SetupGroupId,
  counts: SetupCounts,
  entries: z.array(SetupEntry),
});
export type SetupGroup = z.infer<typeof SetupGroup>;

/** An uploaded file and its preview, worked out again by Check again. */
export const StartingSetupPreview = z.object({
  id: z.uuid(),
  label: z.string(),
  sha256: z.string(),
  /** Goes up each time the preview is worked out again; Apply names the one it saw. */
  version: z.number().int().positive(),
  uploadedBy: Ref.nullable(),
  uploadedByLabel: z.string(),
  uploadedAt: DateTime,
  checkedAt: DateTime,
  counts: SetupCounts,
  groups: z.array(SetupGroup),
  canApply: z.boolean(),
  /**
   * APP-205: the letter pack the file names, and whether its .zip has been added beside it (the
   * card asks for it until it has). Null when the file names no pack.
   */
  letterPack: z
    .object({ sha256: z.string(), received: z.boolean(), fileName: z.string().nullable() })
    .nullable(),
});
export type StartingSetupPreview = z.infer<typeof StartingSetupPreview>;

/** One application of a file, for the card's History. */
export const StartingSetupApplication = z.object({
  id: z.uuid(),
  label: z.string(),
  sha256: z.string(),
  at: DateTime,
  by: Ref.nullable(),
  byLabel: z.string(),
  outcome: z.enum(['applied', 'no_change']),
  counts: SetupCounts,
  batchId: z.uuid().nullable(),
  /**
   * APP-205: the pack's letters this apply brought, as the worker has left them: held for a person
   * (each says why, under Can't apply yet) and still being checked. Null when it brought none.
   */
  letters: z
    .object({ held: z.number().int().nonnegative(), waiting: z.number().int().nonnegative() })
    .nullable(),
});
export type StartingSetupApplication = z.infer<typeof StartingSetupApplication>;

export const StartingSetupCard = z.object({
  /** MyRewards has gone live: the card offers Export only (D6). */
  live: z.boolean(),
  /** Upload, Check again and Apply are offered (System Admin, not previewing, not live). */
  canApply: z.boolean(),
  current: StartingSetupPreview.nullable(),
  history: z.array(StartingSetupApplication),
});
export type StartingSetupCard = z.infer<typeof StartingSetupCard>;

export const CheckStartingSetup = z.object({ version: z.number().int().positive() }).strict();
export type CheckStartingSetup = z.infer<typeof CheckStartingSetup>;

export const ApplyStartingSetup = z
  .object({
    version: z.number().int().positive(),
    reason: z.string().trim().min(1, 'Say why.').max(2000),
  })
  .strict();
export type ApplyStartingSetup = z.infer<typeof ApplyStartingSetup>;

export const StartingSetupApplied = z.object({
  card: StartingSetupCard,
  checklist: GoLiveChecklist,
  outcome: z.enum(['applied', 'no_change']),
  counts: SetupCounts,
});
export type StartingSetupApplied = z.infer<typeof StartingSetupApplied>;
